import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth.context";
import { ArrowLeft, LogOut, UserCog } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import authService from "@/services/auth.service";
import SimpleLanguageSwitcher from "@/components/SimpleLanguageSwitcher";
import { useTranslation } from "react-i18next";
import AccountSettings from "./AccountSettings";

export default function StandaloneAccountSettings() {
  const { t } = useTranslation("chooseOrg");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(t("errors.logoutFailed"));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - User Info & Branding */}
      <div className="hidden lg:flex lg:w-[440px] lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark flex-col p-8 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-[2%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <div className="mb-10">
            <Link to={"/"}>
              <img className="h-7" src="logo_white.png" alt="" />
            </Link>
          </div>

          {/* User Profile Section */}
          <div className="mb-4">
            <p className="text-primary-foreground/60 text-sm font-medium uppercase tracking-wider mb-4">
              {t("loggedInAs")}
            </p>
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 border-[3px] border-white/30 ring-2 ring-white/10">
                <AvatarImage src={user?.photo} />
                <AvatarFallback className="bg-card text-primaryDark text-xl font-semibold">
                  {getInitials(user?.names || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-base truncate">
                  {user?.name || "User"}
                </h3>
                <p className="text-white/70 text-sm truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-2"></div>

          {/* Action Links */}
          <div className="space-y-3 mt-4 flex-1">
            {/* Back Button */}
            <button
              onClick={handleBack}
              className="w-full flex items-center gap-3 px-4 py-3.5 border border-slate-100/20 rounded-md text-white hover:bg-card/10 transition-colors text-left group"
            >
              <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-sm leading-4">
                  {t("goBack", { defaultValue: "Go Back" })}
                </span>
                <span className="text-white/60 text-xs">
                  {t("returnToPrevious", {
                    defaultValue: "Return to previous page",
                  })}
                </span>
              </div>
            </button>

            {/* Current Page Indicator */}
            <div className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md bg-card/10 text-white text-left">
              <UserCog className="h-4 w-4" />
              <span className="text-sm font-medium">
                {t("accountSettings")}
              </span>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center border border-slate-100/20 gap-3 px-4 py-3.5 rounded-md text-white hover:bg-card/10 bg-card/5 transition-colors text-left group"
            >
              <div className="h-9 w-9 rounded-md bg-card/10 flex items-center justify-center group-hover:bg-card/20 transition-colors">
                <LogOut className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-medium text-sm leading-4">
                  {t("signOut")}
                </span>
                <span className="text-white/60 text-xs">{t("endSession")}</span>
              </div>
            </button>
          </div>

          {/* Footer */}
          <div className="pt-6 mt-auto">
            <p className="text-white/40 text-sm">
              {t("footerRights", { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Account Settings Content */}
      <div className="flex-1 flex flex-col bg-backgroud lg:ml-[440px]">
        {/* Mobile Header */}
        <header className="flex items-center justify-between px-4 py-3 bg-card border-b border-border lg:hidden">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="rounded-md"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Link to={"/"}>
              <img className="h-7" src="logo_white.png" alt="" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <SimpleLanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-md text-muted-foreground"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-3xl mx-auto">
            <AccountSettings />
          </div>
        </div>
      </div>
    </div>
  );
}
